export type PackageCategory =
  | "التقييم العميق"
  | "الطب الدقيق"
  | "الخلل الأيضي"
  | "Second Opinion"
  | "الجينات والDNA"
  | "الطب النفسي"
  | "هشاشة العظام"
  | "العلاجات الوريدية"
  | "الوقاية العصبية"
  | "الرعاية الدولية"
  | "VIP";

export interface MedicalPackage {
  id: string;
  name: string;
  nameEn?: string;
  category: PackageCategory;
  categoryEn?: string;
  price?: number;
  originalPrice?: number;
  showPrice?: boolean;
  badge?: string;
  badgeEn?: string;
  icon: string;
  features: string[];
  featuresEn?: string[];
  accentColor: string;
  description?: string;
  descriptionEn?: string;
  duration?: string;
  durationEn?: string;
  targetAudience?: string;
  targetAudienceEn?: string;
  preparation?: string[];
  includes?: { label: string; icon: string }[];
  faqs?: { q: string; a: string }[];
  status?: string;
  is_active?: boolean;
}

export const packageCategories: PackageCategory[] = [
  "التقييم العميق",
  "الطب الدقيق",
  "الخلل الأيضي",
  "Second Opinion",
  "الجينات والDNA",
  "الطب النفسي",
  "هشاشة العظام",
  "العلاجات الوريدية",
  "الوقاية العصبية",
  "الرعاية الدولية",
  "VIP",
];

export const allPackages: MedicalPackage[] = [
  // ── التقييم العميق ──────────────────────────────────────────
  {
    id: "deep-basic",
    name: "التقييم العميق الأساسي",
    category: "التقييم العميق",
    price: 800,
    icon: "ri-focus-3-line",
    accentColor: "teal",
    features: ["جلسة تقييم 90 دقيقة", "تحاليل وظيفية أساسية", "قراءة المخاطر الحالية", "تقرير سريري مفصل"],
  },
  {
    id: "deep-standard",
    name: "التقييم العميق الشامل",
    category: "التقييم العميق",
    price: 1400,
    originalPrice: 1800,
    badge: "الأكثر طلباً",
    icon: "ri-stack-line",
    accentColor: "teal",
    features: ["جلسة تقييم 120 دقيقة", "تحاليل وظيفية وأيضية متقدمة", "قراءة المخاطر الحالية والمستقبلية", "استشارة استشاري أول", "تقرير سريري + خطة شخصية", "متابعة شهر"],
    description: "التقييم العميق الشامل هو جوهر تجربة ذا مديكال أفينيو. جلسة 120 دقيقة مع استشاري أول يفكك فيها الحالة ويقرأ ما وراء العرض، ويربط بين الحاضر الصحي والمخاطر المستقبلية.",
    duration: "120 دقيقة + متابعة شهر",
    targetAudience: "مناسب لمن لم تمنحهم الزيارات السريعة صورة واضحة، ويحتاجون تقييمًا متعدد الطبقات",
    preparation: ["إحضار جميع التقارير الطبية السابقة", "قائمة الأدوية الحالية", "الصيام 8 ساعات للتحاليل", "ارتداء ملابس مريحة"],
    includes: [
      { label: "جلسة تقييم 120 دقيقة", icon: "ri-time-line" },
      { label: "تحاليل وظيفية وأيضية متقدمة", icon: "ri-flask-line" },
      { label: "قراءة المخاطر المستقبلية", icon: "ri-radar-line" },
      { label: "استشارة استشاري أول", icon: "ri-user-star-line" },
      { label: "تقرير سريري مفصل", icon: "ri-file-chart-line" },
      { label: "خطة شخصية قابلة للتطبيق", icon: "ri-route-line" },
      { label: "متابعة شهر كامل", icon: "ri-calendar-check-line" },
    ],
    faqs: [
      { q: "ما الذي يحدث في جلسة التقييم العميق؟", a: "جلسة 120 دقيقة مع استشاري أول يستمع فيها لتاريخك الصحي الكامل، يحلل التحاليل، يقرأ المخاطر الحالية والمستقبلية، ويضع خطة شخصية مبنية على بيولوجيتك الخاصة." },
      { q: "هل أحتاج للصيام قبل الجلسة؟", a: "نعم، الصيام 8 ساعات مطلوب للتحاليل الوظيفية والأيضية. شرب الماء مسموح." },
    ],
  },
  {
    id: "deep-premium",
    name: "التقييم العميق المتكامل",
    category: "التقييم العميق",
    price: 2200,
    originalPrice: 2800,
    badge: "مميز",
    icon: "ri-award-line",
    accentColor: "teal",
    features: ["جلسة تقييم 120 دقيقة", "تحاليل وظيفية شاملة (30+ تحليل)", "تقييم العمر البيولوجي", "قراءة المخاطر المستقبلية", "استشارة 2 استشاري", "خطة شخصية مفصلة", "متابعة 3 أشهر"],
  },
  {
    id: "deep-family",
    name: "التقييم العميق للزوجين",
    category: "التقييم العميق",
    price: 2400,
    originalPrice: 3200,
    icon: "ri-group-line",
    accentColor: "teal",
    features: ["جلستا تقييم لشخصين", "تحاليل وظيفية لكل منهما", "قراءة المخاطر المشتركة", "خطتان شخصيتان", "متابعة 3 أشهر"],
  },

  // ── الطب الدقيق ────────────────────────────────────────────
  {
    id: "precision-basic",
    name: "منصة الطب الدقيق الأساسية",
    category: "الطب الدقيق",
    price: 1800,
    icon: "ri-dna-line",
    accentColor: "violet",
    features: ["تقييم عميق 90 دقيقة", "DNA Risk Score أساسي (20 مرض)", "تحاليل وظيفية متقدمة", "تقرير + خطة وقائية"],
  },
  {
    id: "precision-standard",
    name: "منصة الطب الدقيق الشاملة",
    category: "الطب الدقيق",
    price: 2800,
    originalPrice: 3500,
    badge: "الأكثر طلباً",
    icon: "ri-radar-line",
    accentColor: "violet",
    features: ["تقييم عميق 120 دقيقة", "DNA Risk Score شامل (50+ مرض)", "تقييم العمر البيولوجي", "تحاليل أيضية ووظيفية", "خطة وقائية شخصية", "متابعة 6 أشهر"],
    description: "منصة الطب الدقيق الشاملة تجمع بين التقييم العميق وDNA Risk Score الشامل وتقييم العمر البيولوجي — لبناء صورة كاملة عن مسارك الصحي المستقبلي.",
    duration: "يوم كامل + متابعة 6 أشهر",
    targetAudience: "مناسب لمن يريد فهمًا حقيقيًا لمخاطره الصحية المستقبلية وخطة وقائية مبنية على بيولوجيته الخاصة",
    preparation: ["إحضار جميع التقارير الطبية السابقة", "الصيام 10 ساعات", "قائمة الأدوية والمكملات الحالية"],
    includes: [
      { label: "تقييم عميق 120 دقيقة", icon: "ri-time-line" },
      { label: "DNA Risk Score شامل (50+ مرض)", icon: "ri-dna-line" },
      { label: "تقييم العمر البيولوجي", icon: "ri-heart-pulse-line" },
      { label: "تحاليل أيضية ووظيفية متقدمة", icon: "ri-flask-line" },
      { label: "خطة وقائية شخصية مفصلة", icon: "ri-route-line" },
      { label: "متابعة 6 أشهر", icon: "ri-calendar-check-line" },
    ],
    faqs: [
      { q: "ما الفرق بين DNA Risk Score الأساسي والشامل؟", a: "الأساسي يغطي 20 مرضًا رئيسيًا، بينما الشامل يغطي أكثر من 50 مرضًا ومخاطرة صحية مستقبلية." },
      { q: "كم يستغرق الحصول على نتائج DNA؟", a: "من 10 إلى 14 يومًا من تاريخ أخذ العينة." },
    ],
  },
  {
    id: "precision-premium",
    name: "منصة الطب الدقيق المتقدمة",
    category: "الطب الدقيق",
    price: 4500,
    originalPrice: 5800,
    badge: "حصري",
    icon: "ri-focus-3-line",
    accentColor: "violet",
    features: ["تقييم عميق متعدد التخصصات", "DNA Risk Score + DNA نفسي", "تقييم العمر البيولوجي", "تحاليل الميتوكوندريا", "خطة وقائية شاملة", "متابعة سنة كاملة", "Second Opinion دولي عند الحاجة"],
  },

  // ── الخلل الأيضي ───────────────────────────────────────────
  {
    id: "metabolic-basic",
    name: "تقييم الخلل الأيضي",
    category: "الخلل الأيضي",
    price: 900,
    icon: "ri-scales-line",
    accentColor: "orange",
    features: ["جلسة تقييم أيضي 90 دقيقة", "تحاليل MASLD الأساسية", "تقييم مقاومة الأنسولين", "خطة علاجية أولية"],
  },
  {
    id: "metabolic-standard",
    name: "باقة السمنة والخلل الأيضي",
    category: "الخلل الأيضي",
    price: 2200,
    originalPrice: 2800,
    badge: "الأكثر طلباً",
    icon: "ri-flask-line",
    accentColor: "orange",
    features: ["تقييم أيضي عميق 120 دقيقة", "تحاليل MASLD المتخصصة", "Fibroscan الكبد", "التقييم الجيني الأيضي", "خطة علاجية شخصية", "متابعة 3 أشهر"],
  },
  {
    id: "metabolic-masld",
    name: "باقة MASLD المتخصصة",
    category: "الخلل الأيضي",
    price: 1800,
    originalPrice: 2300,
    icon: "ri-heart-pulse-line",
    accentColor: "orange",
    features: ["تقييم MASLD متخصص", "Fibroscan + تحاليل كبد متقدمة", "تقييم الالتهاب المزمن", "خطة علاجية مخصصة", "متابعة شهرين"],
  },
  {
    id: "metabolic-premium",
    name: "باقة الخلل الأيضي الشاملة",
    category: "الخلل الأيضي",
    price: 3500,
    originalPrice: 4500,
    badge: "مميز",
    icon: "ri-dna-line",
    accentColor: "orange",
    features: ["تقييم أيضي عميق متعدد الطبقات", "MASLD + تحاليل شاملة", "التقييم الجيني الأيضي", "بروتوكول وريدي أيضي", "خطة علاجية شاملة", "متابعة 6 أشهر"],
  },

  // ── Second Opinion ──────────────────────────────────────────
  {
    id: "2op-basic",
    name: "Second Opinion — مراجعة الملف",
    category: "Second Opinion",
    price: 1500,
    icon: "ri-global-line",
    accentColor: "teal",
    features: ["إعداد الملف السريري", "ترجمة طبية متخصصة", "إرسال لمركز دولي", "رأي طبي مكتوب", "جلسة شرح النتائج"],
    duration: "7–14 يوم",
  },
  {
    id: "2op-standard",
    name: "Second Opinion — استشارة مرئية",
    category: "Second Opinion",
    price: 2500,
    originalPrice: 3200,
    badge: "الأكثر طلباً",
    icon: "ri-video-line",
    accentColor: "teal",
    features: ["إعداد الملف السريري", "ترجمة طبية متخصصة", "إرسال لمركز دولي", "استشارة مرئية مع الطبيب الدولي", "رأي طبي مكتوب", "جلسة شرح + خطة عمل"],
    description: "Second Opinion مع استشارة مرئية مباشرة مع الطبيب الدولي — لأن بعض الحالات تحتاج حوارًا حقيقيًا لا مجرد رأي مكتوب.",
    duration: "7–14 يوم",
    targetAudience: "مناسب للحالات المعقدة التي تحتاج حوارًا مباشرًا مع الطبيب الدولي",
    includes: [
      { label: "إعداد الملف السريري الكامل", icon: "ri-file-text-line" },
      { label: "ترجمة طبية متخصصة", icon: "ri-translate-2-line" },
      { label: "إرسال لمركز دولي متخصص", icon: "ri-global-line" },
      { label: "استشارة مرئية مع الطبيب الدولي", icon: "ri-video-line" },
      { label: "رأي طبي مكتوب مفصل", icon: "ri-file-chart-line" },
      { label: "جلسة شرح النتائج + خطة عمل", icon: "ri-chat-3-line" },
    ],
    faqs: [
      { q: "ما المراكز الدولية التي تتعاملون معها؟", a: "نتعامل مع مراكز متخصصة في الولايات المتحدة وجنوب كوريا وألمانيا وغيرها، حسب طبيعة الحالة والتخصص المطلوب." },
    ],
  },
  {
    id: "2op-premium",
    name: "Second Opinion الشامل",
    category: "Second Opinion",
    price: 3500,
    originalPrice: 4500,
    badge: "حصري",
    icon: "ri-route-line",
    accentColor: "teal",
    features: ["إعداد ملف سريري متكامل", "ترجمة طبية متخصصة", "إرسال لمركزين دوليين", "استشارة مرئية مع الطبيبين", "رأيان طبيان مكتوبان", "جلسة مقارنة الآراء + خطة عمل", "دعم لوجستي للسفر عند الحاجة"],
  },

  // ── الجينات والDNA ──────────────────────────────────────────
  {
    id: "dna-basic",
    name: "DNA Risk Score الأساسي",
    category: "الجينات والDNA",
    price: 1800,
    icon: "ri-dna-line",
    accentColor: "violet",
    features: ["تحليل DNA (20 مرض رئيسي)", "تقرير المخاطر الجينية", "جلسة تفسير النتائج", "توصيات وقائية"],
    duration: "10–14 يوم",
  },
  {
    id: "dna-standard",
    name: "DNA Risk Score الشامل",
    category: "الجينات والDNA",
    price: 2800,
    originalPrice: 3500,
    badge: "الأكثر طلباً",
    icon: "ri-radar-line",
    accentColor: "violet",
    features: ["تحليل DNA (50+ مرض)", "تقرير مخاطر جيني شامل", "جلسة تفسير 60 دقيقة", "خطة وقائية شخصية", "متابعة 3 أشهر"],
    duration: "10–14 يوم",
  },
  {
    id: "dna-psych",
    name: "DNA النفسي",
    category: "الجينات والDNA",
    price: 1500,
    icon: "ri-mental-health-line",
    accentColor: "violet",
    features: ["تحليل الاستعداد الجيني النفسي", "تقرير المخاطر النفسية الجينية", "جلسة تفسير مع استشاري نفسي", "خطة وقائية نفسية"],
    duration: "10–14 يوم",
  },
  {
    id: "dna-complete",
    name: "باقة DNA الشاملة",
    category: "الجينات والDNA",
    price: 4200,
    originalPrice: 5500,
    badge: "حصري",
    icon: "ri-focus-3-line",
    accentColor: "violet",
    features: ["DNA Risk Score شامل (50+ مرض)", "DNA النفسي", "تقييم العمر البيولوجي", "جلسة تفسير شاملة 90 دقيقة", "خطة وقائية متكاملة", "متابعة 6 أشهر"],
    duration: "14–21 يوم",
  },

  // ── الطب النفسي ────────────────────────────────────────────
  {
    id: "psych-basic",
    name: "التقييم النفسي العميق",
    category: "الطب النفسي",
    price: 900,
    icon: "ri-mental-health-line",
    accentColor: "rose",
    features: ["جلسة تقييم نفسي 90 دقيقة", "تقييم الوظائف المعرفية", "تقرير سريري نفسي", "خطة علاجية أولية"],
  },
  {
    id: "psych-standard",
    name: "باقة الرعاية النفسية",
    category: "الطب النفسي",
    price: 2500,
    originalPrice: 3200,
    badge: "الأكثر طلباً",
    icon: "ri-brain-line",
    accentColor: "rose",
    features: ["تقييم نفسي عميق 90 دقيقة", "DNA النفسي", "تقييم الوظائف المعرفية", "4 جلسات متابعة", "خطة علاجية شخصية"],
  },
  {
    id: "psych-premium",
    name: "باقة الرعاية النفسية الشاملة",
    category: "الطب النفسي",
    price: 3500,
    originalPrice: 4500,
    badge: "مميز",
    icon: "ri-user-heart-line",
    accentColor: "rose",
    features: ["تقييم نفسي عميق متعدد الطبقات", "DNA النفسي", "تقييم معرفي شامل", "6 جلسات متابعة", "خطة علاجية شاملة", "متابعة 3 أشهر"],
  },

  // ── هشاشة العظام ───────────────────────────────────────────
  {
    id: "osteo-basic",
    name: "تقييم هشاشة العظام",
    category: "هشاشة العظام",
    price: 700,
    icon: "ri-body-scan-line",
    accentColor: "amber",
    features: ["جلسة تقييم 90 دقيقة", "DEXA كثافة العظام", "تحاليل الأيض العظمي", "تقرير + توصيات"],
  },
  {
    id: "osteo-standard",
    name: "باقة هشاشة العظام المكثفة",
    category: "هشاشة العظام",
    price: 2500,
    originalPrice: 3200,
    badge: "الأكثر طلباً",
    icon: "ri-scan-line",
    accentColor: "amber",
    features: ["تقييم عميق 90 دقيقة", "DEXA + تحاليل أيض عظمي شاملة", "التقييم الجيني لهشاشة العظام", "تقييم مخاطر الكسور FRAX", "خطة علاجية مكثفة", "متابعة 6 أشهر"],
  },
  {
    id: "osteo-premium",
    name: "باقة هشاشة العظام الشاملة",
    category: "هشاشة العظام",
    price: 4000,
    originalPrice: 5200,
    badge: "مميز",
    icon: "ri-shield-flash-line",
    accentColor: "amber",
    features: ["تقييم عميق متخصص", "DEXA + تحاليل شاملة", "التقييم الجيني", "العلاجات البيولوجية المتخصصة", "خطة علاجية مكثفة", "متابعة سنة كاملة"],
  },

  // ── العلاجات الوريدية ──────────────────────────────────────
  {
    id: "iv-basic",
    name: "جلسة وريدية مخصصة",
    category: "العلاجات الوريدية",
    price: 450,
    icon: "ri-drop-line",
    accentColor: "cyan",
    features: ["تحاليل ما قبل الجلسة", "بروتوكول وريدي مخصص", "جلسة 60–90 دقيقة", "تقرير ما بعد الجلسة"],
  },
  {
    id: "iv-energy",
    name: "باقة الطاقة الخلوية",
    category: "العلاجات الوريدية",
    price: 3200,
    originalPrice: 4000,
    badge: "الأكثر طلباً",
    icon: "ri-flashlight-line",
    accentColor: "cyan",
    features: ["تحاليل وظيفية قبل البدء", "5 جلسات بروتوكول الطاقة الخلوية", "دعم الميتوكوندريا", "مضادات الأكسدة العالية", "متابعة شهر"],
  },
  {
    id: "iv-metabolic",
    name: "باقة الدعم الأيضي الوريدي",
    category: "العلاجات الوريدية",
    price: 2800,
    originalPrice: 3500,
    icon: "ri-scales-line",
    accentColor: "cyan",
    features: ["تحاليل أيضية قبل البدء", "5 جلسات دعم أيضي مخصصة", "تحسين حساسية الأنسولين", "دعم صحة الكبد", "متابعة شهر"],
  },
  {
    id: "iv-premium",
    name: "باقة العلاجات الوريدية الشاملة",
    category: "العلاجات الوريدية",
    price: 5800,
    originalPrice: 7500,
    badge: "مميز",
    icon: "ri-heart-pulse-line",
    accentColor: "cyan",
    features: ["تحاليل وظيفية وأيضية شاملة", "10 جلسات بروتوكولات مخصصة", "دعم الميتوكوندريا + الأيض + الأعصاب", "متابعة شهرين", "تعديل البروتوكول حسب الاستجابة"],
  },

  // ── الوقاية العصبية ────────────────────────────────────────
  {
    id: "neuro-basic",
    name: "تقييم مخاطر الزهايمر",
    category: "الوقاية العصبية",
    price: 900,
    icon: "ri-brain-line",
    accentColor: "violet",
    features: ["جلسة تقييم 90 دقيقة", "تقييم الوظائف المعرفية", "تحاليل المؤشرات العصبية", "تقرير + توصيات وقائية"],
  },
  {
    id: "neuro-standard",
    name: "باقة الوقاية من الزهايمر",
    category: "الوقاية العصبية",
    price: 3800,
    originalPrice: 4800,
    badge: "الأكثر طلباً",
    icon: "ri-shield-flash-line",
    accentColor: "violet",
    features: ["تقييم عميق 90 دقيقة", "التقييم الجيني للزهايمر (APOE)", "MRI دماغ متقدم", "تحاليل المؤشرات الحيوية العصبية", "خطة وقائية شخصية", "متابعة سنة"],
  },
  {
    id: "neuro-premium",
    name: "باقة الوقاية العصبية الشاملة",
    category: "الوقاية العصبية",
    price: 5500,
    originalPrice: 7000,
    badge: "حصري",
    icon: "ri-focus-3-line",
    accentColor: "violet",
    features: ["تقييم عميق متعدد التخصصات", "DNA Risk Score + جيني عصبي", "MRI دماغ وظيفي", "تحاليل شاملة", "بروتوكول وريدي عصبي", "خطة وقائية شاملة", "متابعة سنة كاملة"],
  },

  // ── الرعاية الدولية ────────────────────────────────────────
  {
    id: "global-basic",
    name: "الرعاية الدولية الأساسية",
    category: "الرعاية الدولية",
    price: 3500,
    icon: "ri-global-line",
    accentColor: "teal",
    features: ["تقييم عميق محلي", "DNA Risk Score", "Second Opinion من مركز دولي", "خطة علاجية متكاملة", "متابعة 6 أشهر"],
  },
  {
    id: "global-standard",
    name: "الرعاية الدولية المتكاملة",
    category: "الرعاية الدولية",
    price: 5500,
    originalPrice: 7000,
    badge: "الأكثر طلباً",
    icon: "ri-route-line",
    accentColor: "teal",
    features: ["تقييم عميق متعدد التخصصات", "DNA Risk Score + DNA نفسي", "Second Opinion من مراكز USA وKorea", "استشارة مرئية دولية", "خطة علاجية شاملة", "متابعة سنة كاملة", "ربط مستمر بمؤشرات الحالة"],
    description: "الرعاية الدولية المتكاملة هي أعلى مستويات الرعاية في ذا مديكال أفينيو — تجمع بين العمق السريري المحلي والخبرة الدولية في USA وKorea.",
    duration: "شهر + متابعة سنة كاملة",
    targetAudience: "مناسب للحالات المعقدة التي تحتاج منظورًا دوليًا أوسع من الإطار المحلي",
    includes: [
      { label: "تقييم عميق متعدد التخصصات", icon: "ri-stack-line" },
      { label: "DNA Risk Score + DNA نفسي", icon: "ri-dna-line" },
      { label: "Second Opinion من USA وKorea", icon: "ri-global-line" },
      { label: "استشارة مرئية مع الطبيب الدولي", icon: "ri-video-line" },
      { label: "خطة علاجية شاملة ومتكاملة", icon: "ri-route-line" },
      { label: "متابعة سنة كاملة", icon: "ri-calendar-check-line" },
      { label: "تطبيق مراقبة صحية شخصي", icon: "ri-smartphone-line" },
    ],
    faqs: [
      { q: "ما المراكز الدولية المشمولة؟", a: "نتعامل مع مراكز متخصصة في الولايات المتحدة وجنوب كوريا وألمانيا، حسب طبيعة الحالة والتخصص المطلوب." },
      { q: "هل يمكن السفر للعلاج في الخارج؟", a: "نعم، نوفر دعمًا لوجستيًا كاملًا لمن يحتاج السفر للعلاج في الخارج." },
    ],
  },
  {
    id: "global-premium",
    name: "الرعاية الدولية الشاملة",
    category: "الرعاية الدولية",
    price: 8500,
    originalPrice: 11000,
    badge: "الأرقى",
    icon: "ri-medal-line",
    accentColor: "teal",
    features: ["تقييم عميق شامل", "DNA كامل + عمر بيولوجي", "Second Opinion من 3 مراكز دولية", "استشارات مرئية متعددة", "خطة علاجية دولية متكاملة", "متابعة سنتين", "دعم لوجستي للسفر"],
  },

  // ── VIP ────────────────────────────────────────────────────
  {
    id: "vip-basic",
    name: "باقة VIP الطب الدقيق",
    category: "VIP",
    price: 6500,
    originalPrice: 8500,
    badge: "حصري",
    icon: "ri-vip-crown-line",
    accentColor: "amber",
    features: ["تقييم عميق من كبار الاستشاريين", "DNA Risk Score الشامل", "Second Opinion دولي", "خطة علاجية VIP", "متابعة سنة كاملة", "استقبال VIP خاص"],
  },
  {
    id: "vip-executive",
    name: "باقة VIP التنفيذية",
    category: "VIP",
    price: 12000,
    originalPrice: 16000,
    badge: "الأرقى",
    icon: "ri-vip-diamond-line",
    accentColor: "amber",
    features: ["تقييم عميق متعدد التخصصات", "DNA كامل + عمر بيولوجي + ميتوكوندريا", "Second Opinion من مراكز دولية متعددة", "استشارات مرئية دولية", "خطة علاجية شاملة", "متابعة سنتين", "غرفة VIP خاصة", "خدمة منزلية"],
    description: "باقة VIP التنفيذية هي تجربة الطب الدقيق الأرقى — رعاية شخصية كاملة من نخبة الاستشاريين مع امتداد دولي حقيقي وخصوصية تامة.",
    duration: "شهر + متابعة سنتين",
    targetAudience: "مخصصة لكبار المسؤولين والشخصيات البارزة الراغبين في أعلى مستويات الطب الدقيق",
    includes: [
      { label: "تقييم عميق من كبار الاستشاريين", icon: "ri-user-star-line" },
      { label: "DNA كامل + عمر بيولوجي + ميتوكوندريا", icon: "ri-dna-line" },
      { label: "Second Opinion من مراكز دولية", icon: "ri-global-line" },
      { label: "استشارات مرئية دولية متعددة", icon: "ri-video-line" },
      { label: "غرفة VIP خاصة", icon: "ri-hotel-line" },
      { label: "متابعة سنتين كاملتين", icon: "ri-calendar-check-line" },
      { label: "خدمة منزلية عند الحاجة", icon: "ri-home-heart-line" },
    ],
    faqs: [
      { q: "هل تشمل الخدمة المنزلية أخذ التحاليل في المنزل؟", a: "نعم، يمكن إرسال فريق طبي متخصص لأخذ التحاليل في منزلك أو مكتبك." },
      { q: "هل يمكن تحديد موعد خارج أوقات الدوام الرسمي؟", a: "نعم، نوفر مواعيد مرنة تناسب جدولك بما في ذلك المساء وعطل نهاية الأسبوع." },
    ],
  },
  {
    id: "vip-corporate",
    name: "باقة الشركات VIP",
    category: "VIP",
    price: 18000,
    originalPrice: 25000,
    badge: "للشركات",
    icon: "ri-building-line",
    accentColor: "amber",
    features: ["تقييم عميق لـ 5 مديرين تنفيذيين", "DNA Risk Score لكل منهم", "Second Opinion دولي عند الحاجة", "تقارير سرية لكل فرد", "متابعة سنة كاملة", "أولوية قصوى في الحجز"],
  },
];
